import { useState, useCallback, useRef } from 'react';
import { getSessionStorage } from '../../utils';
import constant from '../../constant';
import { v4 as uuidv4 } from 'uuid';

const AI_BASE_URL = process.env.REACT_APP_AI_URL || 'http://localhost:4001';

/**
 * Reads the JWT token from session storage — same method the rest of the app uses.
 */
const getToken = () => {
  try {
    return getSessionStorage(constant.keys.userToken);
  } catch {
    return null;
  }
};

/**
 * useAIChat — manages conversation state and streams AI responses via SSE.
 */
export function useAIChat() {
  const [messages, setMessages]       = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [capabilities, setCapabilities] = useState(null);
  const sessionId = useRef(uuidv4());
  const abortRef  = useRef(null);

  // Load capabilities once (what this user's role can do)
  const loadCapabilities = useCallback(async () => {
    const token = getToken();
    if (!token || capabilities) return;
    try {
      const res = await fetch(`${AI_BASE_URL}/api/ai/capabilities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCapabilities(await res.json());
    } catch { /* non-fatal */ }
  }, [capabilities]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isStreaming) return;

    const token = getToken();
    if (!token) return;

    const userMsg      = { id: uuidv4(), role: 'user', content: text };
    const assistantMsg = { id: uuidv4(), role: 'assistant', content: '', intent: null, loading: true };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setIsStreaming(true);

    // Build history from the last 10 messages
    const history = messages.slice(-10).map(({ role, content }) => ({ role, content }));

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const res = await fetch(`${AI_BASE_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: text, sessionId: sessionId.current, history }),
        signal: controller.signal,
      });

      if (!res.ok) throw new Error('Request failed');

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            setMessages((prev) => {
              const msgs = [...prev];
              const idx  = msgs.findIndex((m) => m.id === assistantMsg.id);
              if (idx === -1) return msgs;
              const msg = { ...msgs[idx] };
              if (payload.intent)  msg.intent   = payload.intent;
              if (payload.text)    msg.content  += payload.text;
              if (payload.message) msg.content   = payload.message; // error fallback
              msgs[idx] = msg;
              return msgs;
            });
          } catch { /* malformed line — skip */ }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages((prev) => {
          const msgs = [...prev];
          const idx  = msgs.findIndex((m) => m.id === assistantMsg.id);
          if (idx === -1) return msgs;
          msgs[idx] = { ...msgs[idx], content: 'Something went wrong. Please try again.', loading: false };
          return msgs;
        });
      }
    } finally {
      setMessages((prev) => {
        const msgs = [...prev];
        const idx  = msgs.findIndex((m) => m.id === assistantMsg.id);
        if (idx !== -1) msgs[idx] = { ...msgs[idx], loading: false };
        return msgs;
      });
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [messages, isStreaming]);

  const stopStreaming = useCallback(() => abortRef.current?.abort(), []);

  const clearHistory = useCallback(() => {
    setMessages([]);
    sessionId.current = uuidv4();
  }, []);

  return { messages, sendMessage, isStreaming, stopStreaming, clearHistory, capabilities, loadCapabilities };
}
